import type { BlueprintProofStateDto } from './blueprint-proof-state';
import { isBlueprintProductReadyProof } from './blueprint-proof-state';

export type BlueprintProvenanceSourceClassDto =
  | 'fixture'
  | 'synthetic-demo'
  | 'local-script'
  | 'product-synthetic'
  | 'operator-extract';

export type BlueprintRunModeDto = 'fixture-test' | 'product-synthetic' | 'product-operator';

export type BlueprintRuntimeProvenanceDto = {
  runMode: BlueprintRunModeDto;
  sourceClass: BlueprintProvenanceSourceClassDto;
  proofState: BlueprintProofStateDto;
  objectId: string;
};

export type BlueprintFixtureLeakDto = {
  objectId: string;
  reason: string;
  path?: string;
  value?: string;
};

export type BlueprintFixtureLeakGuardOptionsDto = {
  governedPath?: string;
  payload?: unknown;
  fixtureTokens?: readonly RegExp[];
};

export const BLUEPRINT_FORBIDDEN_FIXTURE_VALUE_PATTERNS: readonly RegExp[] = [
  /\bqtc-erp-synth-[a-z0-9-]+\b/i,
  /\bfixture-test-proof\b/i,
  /\bfixture-test\b/i,
  /\bsynthetic-demo\b/i,
  /\bfixture:/i,
] as const;

const NON_OPERATOR_SOURCE_CLASSES: ReadonlySet<BlueprintProvenanceSourceClassDto> = new Set([
  'fixture',
  'synthetic-demo',
  'local-script',
]);

export function isBlueprintProvenanceSourceClass(
  value: unknown,
): value is BlueprintProvenanceSourceClassDto {
  return (
    value === 'fixture' ||
    value === 'synthetic-demo' ||
    value === 'local-script' ||
    value === 'product-synthetic' ||
    value === 'operator-extract'
  );
}

export function isBlueprintRunMode(value: unknown): value is BlueprintRunModeDto {
  return value === 'fixture-test' || value === 'product-synthetic' || value === 'product-operator';
}

export function detectBlueprintFixtureLeak(
  provenance: BlueprintRuntimeProvenanceDto,
  options: BlueprintFixtureLeakGuardOptionsDto = {},
): BlueprintFixtureLeakDto | null {
  const { objectId, runMode, sourceClass, proofState } = provenance;

  const payloadLeak = findBlueprintFixtureValueLeak(
    options.payload,
    options.fixtureTokens ?? BLUEPRINT_FORBIDDEN_FIXTURE_VALUE_PATTERNS,
  );
  if (payloadLeak) {
    return {
      objectId,
      path: options.governedPath ? `${options.governedPath}.${payloadLeak.path}` : payloadLeak.path,
      value: payloadLeak.value,
      reason: `governed payload contains fixture value '${payloadLeak.value}'`,
    };
  }

  if (runMode !== 'fixture-test' && NON_OPERATOR_SOURCE_CLASSES.has(sourceClass)) {
    return {
      objectId,
      reason: `product run mode '${runMode}' cannot carry '${sourceClass}' provenance`,
    };
  }

  if (sourceClass !== 'operator-extract' && isBlueprintProductReadyProof(proofState)) {
    return {
      objectId,
      reason: `proof state '${proofState}' requires operator-extract provenance, got '${sourceClass}'`,
    };
  }

  if (runMode === 'product-operator' && sourceClass !== 'operator-extract') {
    return {
      objectId,
      reason: `operator run requires operator-extract provenance, got '${sourceClass}'`,
    };
  }

  if (runMode === 'product-synthetic' && proofState === 'operator-runtime-proof') {
    return {
      objectId,
      reason: 'synthetic run cannot claim operator-runtime-proof',
    };
  }

  return null;
}

export function assertNoBlueprintFixtureLeak(
  provenance: BlueprintRuntimeProvenanceDto,
  options: BlueprintFixtureLeakGuardOptionsDto = {},
): void {
  const leak = detectBlueprintFixtureLeak(provenance, options);
  if (leak) {
    const path = leak.path ? ` at ${leak.path}` : '';
    throw new Error(`Blueprint fixture leak: ${leak.reason}${path} (object ${leak.objectId})`);
  }
}

function findBlueprintFixtureValueLeak(
  value: unknown,
  fixtureTokens: readonly RegExp[],
  path = '$',
  seen: WeakSet<object> = new WeakSet(),
): { path: string; value: string } | null {
  if (typeof value === 'string') {
    if (fixtureTokens.some((pattern) => pattern.test(value))) {
      return { path, value };
    }
    return null;
  }

  if (!value || typeof value !== 'object') {
    return null;
  }

  if (seen.has(value)) {
    return null;
  }
  seen.add(value);

  if (Array.isArray(value)) {
    for (let index = 0; index < value.length; index += 1) {
      const leak = findBlueprintFixtureValueLeak(value[index], fixtureTokens, `${path}[${index}]`, seen);
      if (leak) {
        return leak;
      }
    }
    return null;
  }

  for (const [key, nestedValue] of Object.entries(value)) {
    const leak = findBlueprintFixtureValueLeak(nestedValue, fixtureTokens, `${path}.${key}`, seen);
    if (leak) {
      return leak;
    }
  }

  return null;
}
